/**
 * parseHy3Entries.ts
 *
 * Parses a Hy-Tek HY3 meet-entries file and returns data in the same shape as
 * parseSd3(), so the existing ImportEntriesModal flow can use the output without
 * modification.
 *
 * HY3 record types handled:
 *   A1  – file header  (ignored beyond confirming file type)
 *   B1  – meet header  (ignored)
 *   B2  – additional meet info (ignored)
 *   C1  – team header  (captures team code / name for the club list)
 *   C2, C3 – team extras (ignored)
 *   D1  – swimmer demographics (athlete number → name/DOB/gender lookup table)
 *   E1  – individual event entry
 *   F1  – relay team entry
 *   F3  – relay swimmer list (4 swimmers per record, referenced by athlete number)
 *   Z0  – file terminator (ignored)
 *
 * Record layout is 128 data bytes + 2 checksum bytes + CRLF.
 * Checksum validation is intentionally skipped on import.
 *
 * Field positions are 0-indexed throughout.
 */

import { MeetEvent } from "../ImportSd3Modals/ImportEntriesModal"
import { ISwimDatum } from "./parseSd3"

// ---------------------------------------------------------------------------
// Types (reuse parseSd3 shape so downstream code is unchanged)
// ---------------------------------------------------------------------------

interface IHy3Swimmer {
  athleteNumber: string
  gender: string          // "M" | "F"
  lastName: string
  firstName: string
  dob: string             // "YYYY-MM-DD"
  club: string            // team code from the associated C1 record
  usasId: string          // USAS/national ID from D1 bytes 68-84
}

interface IHy3Club {
  teamCode: string
  teamFullName: string
}

// Relay team entry as parsed from F1 records before the F3 swimmer list is seen.
interface IPendingRelay {
  teamCode: string
  relayDesignation: string  // "A" | "B" | "C" ...
  eventGender: string
  eventStroke: string
  eventDistance: number
  eventNumber: number
  eventAge: string
  seedTime: string
  seedTimeCourse: string
  isExhibition: string
  alternateDesignation: string
  relayLegs: {
    swimmerLastName: string
    swimmerFirstName: string
    swimmerUSAId: string
    swimmerGender: string
    swimmerDOB: string
    teamPosition: number
  }[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert HY3 stroke code to DB long_name used in strokeMapping table. */
function hy3StrokeToDb(code: string, isRelay: boolean): string {
  if (isRelay) {
    switch (code) {
      case "A": return "Freestyle Relay"
      case "B": return "Backstroke Relay"
      case "C": return "Breaststroke Relay"
      case "D": return "Butterfly Relay"
      case "E": return "Medley Relay"
      case "F": return "Individual Medley Relay"
      default:  return code.trim() + " Relay"
    }
  }
  switch (code) {
    case "A": return "Freestyle"
    case "B": return "Backstroke"
    case "C": return "Breaststroke"
    case "D": return "Butterfly"
    case "E": return "Individual Medley"
    default:  return "Freestyle"
  }
}

/**
 * Map HY3 age-group min/max integers to the display string stored in
 * meet_event_orders.age_group.
 *
 * HY3 encodes min at E1 pos 23-24, max at E1 pos 26-27 (0-indexed), as
 * two-digit strings separated by a space.  A value of 0 for min means "no
 * lower bound"; 99 for max means "no upper bound / open".
 */
function hy3AgeGroupToDb(minStr: string, maxStr: string): string {
  const min = parseInt(minStr.trim(), 10) || 0
  const max = parseInt(maxStr.trim(), 10) || 0

  // 109 is the HY3 sentinel for "no upper bound"
  if (min === 0 && (max === 0 || max >= 109)) return "Open"
  if (min === 0 && max > 0)   return `${max} & Under`
  if (max === 0 || max >= 109) return `${min} & Over`
  if (min === max)             return String(min)
  return `${min}-${max}`
}

/**
 * Convert a Hy-Tek DOB string (MMDDYYYY, 8 chars) to "YYYY-MM-DD".
 * Returns "" when the input cannot be parsed.
 */
function parseDob(raw: string): string {
  if (raw.length !== 8) return ""
  const mm = raw.slice(0, 2)
  const dd = raw.slice(2, 4)
  const yyyy = raw.slice(4, 8)
  if (!/^\d{8}$/.test(raw)) return ""
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Convert a Hy-Tek decimal-seconds time string (right-justified in 8 chars,
 * "0.00" = NT) to the format expected by createMeetEntries:
 *   - "NT" when the time is zero
 *   - raw decimal-seconds string otherwise (createMeetEntries stores it as-is)
 */
function hy3TimeToSeedTime(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed || trimmed === "0.00" || trimmed === "0") return "NT"
  return trimmed
}

/** Map relay letter to relayTeam integer (same as parseSd3 relayMap). */
const relayLetterMap: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  I: 9, J: 10, K: 11, L: 12, M: 13, N: 14, O: 15, P: 16,
  Q: 17, R: 18, S: 19,
}
function relayLetterToNum(letter: string): number {
  return relayLetterMap[letter.toUpperCase()] ?? 20
}

// ---------------------------------------------------------------------------
// HY3 record parsers
// ---------------------------------------------------------------------------

/**
 * Parse a D1 swimmer-demographics record.
 *
 * Layout (0-indexed):
 *   0-1   "D1"
 *   2     gender ('M'/'F')
 *   3-7   athlete number (5 chars, right-justified)
 *   8-27  last name (20 chars, left-justified)
 *   28-47 first name (20 chars, left-justified)
 *   68-83 USAS ID (16 chars)
 *   88-95 DOB MMDDYYYY
 */
function parseD1Record(row: string, athleteMap: Map<string, IHy3Swimmer>, currentTeamCode: string): void {
  const gender        = row.slice(2, 3).trim()
  const athleteNumber = row.slice(3, 8).trim()
  const lastName      = row.slice(8, 28).trim()
  const firstName     = row.slice(28, 48).trim()
  const usasId        = row.length > 68 ? row.slice(68, 84).trim() : ""
  const dobRaw        = row.slice(88, 96).trim()

  if (!athleteNumber) return

  athleteMap.set(athleteNumber, {
    athleteNumber,
    gender,
    lastName,
    firstName,
    dob: parseDob(dobRaw),
    club: currentTeamCode,
    usasId,
  })
}

/**
 * Parse an E1 individual-event-entry record.
 *
 * Layout (0-indexed):
 *   0-1   "E1"
 *   2     swimmer gender
 *   3-7   athlete number (5 chars)
 *   8     entry status code (' '=normal, 'X'=exhibition, 'A'=alt-A, 'B'=alt-B, 'C'=alt-C)
 *   13-14 event gender (2 chars, first char used)
 *   15-20 distance (6 chars, right-justified)
 *   21    stroke code
 *   23-24 age group min (2 chars)
 *   26-27 age group max (2 chars)
 *   38-40 event number (3 chars, right-justified)
 *   50    course ('Y'/'S'/'L')
 *   51-58 entry time in decimal seconds (8 chars, right-justified, "0.00"=NT)
 *   59    entry time course
 */
function parseE1Record(
  row: string,
  athleteMap: Map<string, IHy3Swimmer>,
  swimData: ISwimDatum,
  meetEvents: MeetEvent[],
): void {
  const athleteNumber = row.slice(3, 8).trim()
  const swimmer = athleteMap.get(athleteNumber)
  if (!swimmer) return

  const clubEntry = swimData.clubs.find((c) => c.teamCode === swimmer.club)
  if (!clubEntry) return

  const eventGender    = row.slice(13, 14).trim() || swimmer.gender
  const distance       = parseInt(row.slice(15, 21).trim(), 10)
  const strokeCode     = row.slice(21, 22).trim()
  const ageMin         = row.slice(23, 25).trim()
  const ageMax         = row.slice(25, 28).trim()  // 3-char field; 109 = open
  const eventNumber    = parseInt(row.slice(38, 41).trim(), 10)
  const course         = row.slice(50, 51).trim()
  const timeRaw        = row.slice(51, 59)
  const timeCourse     = row.slice(59, 60).trim()

  const statusCode     = row.slice(8, 9).trim()

  const isExhibition   = statusCode === "X" ? "1" : "0"
  const alternateDesignation =
    statusCode === "A" ? "A" :
    statusCode === "B" ? "B" :
    statusCode === "C" ? "C" : ""

  const eventStroke    = hy3StrokeToDb(strokeCode, false)
  const eventAge       = hy3AgeGroupToDb(ageMin, ageMax)
  const seedTime       = hy3TimeToSeedTime(timeRaw)

  const entry = {
    swimmerTeamPosition: -1,
    relayOnlyRecord: "0",
    isRelay: "0",
    relayTeam: -1,
    teamPosition: -1,
    swimmerLastName: swimmer.lastName,
    swimmerFirstName: swimmer.firstName,
    swimmerUSAId: swimmer.usasId,
    swimmerDOB: swimmer.dob,
    swimmerGender: swimmer.gender,
    eventGender,
    eventStroke,
    eventDistance: distance,
    eventNumber: isNaN(eventNumber) ? -1 : eventNumber,
    eventAge,
    seedTime,
    seedTimeCourse: timeCourse || course || "Y",
    prelimTime: "",
    prelimTimeCourse: "",
    swimOffTime: "",
    swimOffTimeCourse: "",
    finalsTime: "",
    finalsTimeCourse: "",
    prelimHeat: "",
    prelimLane: "",
    prelimPlace: "",
    finalsHeat: "",
    finalsLane: "",
    finalsPlace: "",
    finalsPoints: "",
    isExhibition,
    alternateDesignation,
    round: null,
  }

  clubEntry.individualEntries.push(entry)

  // Update meetEvents list (unique by eventNumber + stroke + distance + gender + age)
  if (!isNaN(eventNumber) && eventNumber > 0) {
    const key = `${eventNumber}`
    if (!meetEvents.some((e) => e.eventNumber === eventNumber)) {
      meetEvents.push({
        distance: String(distance),
        gender: eventGender === "F" ? "Girls" : eventGender === "M" ? "Boys" : "Mixed",
        ageGroup: eventAge,
        eventNumber,
        stroke: eventStroke,
        isRelay: 0,
        course: course === "S" ? "SCM" : course === "L" ? "LCM" : "SCY",
        eventName: `${eventGender === "F" ? "Girls" : "Boys"} ${eventAge} ${distance} ${eventStroke}`,
      })
    }
  }
}

/**
 * Parse an F1 relay-team-entry record.
 *
 * Layout (0-indexed):
 *   0-1   "F1"
 *   2-6   team code (5 chars, left-justified)
 *   7     relay designation ('A'/'B'/'C'...)
 *   8     entry status code (' '=normal, 'X'=exhibition)
 *   12-14 gender codes (3 chars, first char used: 'F'/'M')
 *   15-20 distance (6 chars, right-justified)
 *   21    stroke code
 *   23-24 age group min
 *   26-27 age group max
 *   38-40 event number (3 chars, right-justified)
 *   50    course
 *   51-58 relay time in decimal seconds (8 chars, "0.00"=NT)
 *   59    time course
 */
function parseF1Record(row: string): IPendingRelay | null {
  const teamCode       = row.slice(2, 7).trim()
  const relayDesig     = row.slice(7, 8).trim()
  const eventGender    = row.slice(12, 13).trim()
  const distance       = parseInt(row.slice(15, 21).trim(), 10)
  const strokeCode     = row.slice(21, 22).trim()
  const ageMin         = row.slice(23, 25).trim()
  const ageMax         = row.slice(25, 28).trim()  // 3-char field; 109 = open
  const eventNumber    = parseInt(row.slice(38, 41).trim(), 10)
  const course         = row.slice(50, 51).trim()
  const timeRaw        = row.slice(51, 59)
  const timeCourse     = row.slice(59, 60).trim()

  const statusCode     = row.slice(8, 9).trim()

  const isExhibition   = statusCode === "X" ? "1" : "0"
  const alternateDesignation =
    statusCode === "A" ? "A" :
    statusCode === "B" ? "B" :
    statusCode === "C" ? "C" : ""

  return {
    teamCode,
    relayDesignation: relayDesig || "A",
    eventGender,
    eventStroke: hy3StrokeToDb(strokeCode, true),
    eventDistance: isNaN(distance) ? 0 : distance,
    eventNumber: isNaN(eventNumber) ? -1 : eventNumber,
    eventAge: hy3AgeGroupToDb(ageMin, ageMax),
    seedTime: hy3TimeToSeedTime(timeRaw),
    seedTimeCourse: timeCourse || course || "Y",
    isExhibition,
    alternateDesignation,
    relayLegs: [],
  }
}

/**
 * Parse an F3 relay-swimmer-list record and attach legs to the pending relay.
 *
 * Layout (0-indexed):
 *   0-1   "F3"
 *   Then up to 4 × 13-char blocks:
 *     +0   gender ('F'/'M')
 *     +1–5  athlete number (5 chars, right-justified)
 *     +6–10 last name abbreviated (5 chars)
 *     +11  gender/relay letter (same as gender char)
 *     +12  relay position digit ('1'–'4')
 */
function parseF3Record(
  row: string,
  pendingRelay: IPendingRelay,
  athleteMap: Map<string, IHy3Swimmer>,
): void {
  const blockStart = 2
  const blockSize  = 13
  const numBlocks  = 4

  for (let i = 0; i < numBlocks; i++) {
    const offset       = blockStart + i * blockSize
    if (offset + blockSize > row.length) break

    const block        = row.slice(offset, offset + blockSize)
    const athleteNum   = block.slice(1, 6).trim()
    const positionChar = block.slice(12, 13).trim()

    if (!athleteNum) continue

    const swimmer   = athleteMap.get(athleteNum)
    const position  = parseInt(positionChar, 10)

    if (swimmer) {
      pendingRelay.relayLegs.push({
        swimmerLastName:  swimmer.lastName,
        swimmerFirstName: swimmer.firstName,
        swimmerUSAId:     swimmer.usasId,
        swimmerGender:    swimmer.gender,
        swimmerDOB:       swimmer.dob,
        teamPosition:     isNaN(position) ? i + 1 : position,
      })
    }
  }
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Parse the text content of a .hy3 meet-entries file.
 *
 * Returns data in the same shape as parseSd3() so the existing
 * ImportEntriesModal / createMeetEntries pipeline handles both formats.
 */
export default function parseHy3Entries(hy3Contents: string): {
  clubNames: string[]
  swimmers: {
    club: string
    swimmerFirstName: string
    swimmerLastName: string
    swimmerGender: string
    swimmerDOB: string
    swimmerUSAId: string
    swimmerId: number
  }[]
  swimData: ISwimDatum
  meetEvents: MeetEvent[]
} {
  // Normalise line endings and split into individual records.
  // Each HY3 record is 130 chars (128 data + 2 checksum) terminated by CRLF.
  // Some exports use LF only; handle both.
  const rows = hy3Contents
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((r) => r.trim().length > 0)

  // Build the ISwimDatum skeleton (single "meet")
  const swimData: ISwimDatum = {
    clubs: [],
    orgCode: "",
    filePurpose: "Meet Entries",
    meetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    meetDate: "",
  }

  const clubs: IHy3Club[] = []
  const athleteMap = new Map<string, IHy3Swimmer>()
  const meetEvents: MeetEvent[] = []

  // Track the currently-active team code for D1 records
  let currentTeamCode = ""

  // Track the most-recently-parsed F1 relay (awaiting its F3 swimmer list)
  let pendingRelay: IPendingRelay | null = null

  for (const row of rows) {
    const recordType = row.slice(0, 2)

    switch (recordType) {
      case "C1": {
        const teamCode = row.slice(2, 7).trim()
        const teamName = row.slice(7, 37).trim()
        currentTeamCode = teamCode
        if (teamCode && !clubs.some((c) => c.teamCode === teamCode)) {
          clubs.push({ teamCode, teamFullName: teamName || teamCode })
          swimData.clubs.push({
            teamCode,
            teamFullName: teamName || teamCode,
            teamShortName: teamCode,
            teamCity: "",
            teamState: "",
            teamZipCode: "",
            individualEntries: [],
            relayEntries: [],
          })
        }
        break
      }

      case "D1": {
        parseD1Record(row, athleteMap, currentTeamCode)
        break
      }

      case "E1": {
        // Flush any pending relay without swimmer data before starting individual
        pendingRelay = null
        parseE1Record(row, athleteMap, swimData, meetEvents)
        break
      }

      case "F1": {
        // Flush previous relay if it didn't receive an F3 record
        if (pendingRelay) {
          commitRelay(pendingRelay, swimData, meetEvents)
        }
        pendingRelay = parseF1Record(row)
        break
      }

      case "F3": {
        if (pendingRelay) {
          parseF3Record(row, pendingRelay, athleteMap)
          // Each F1 is followed by exactly one F3, so commit immediately
          commitRelay(pendingRelay, swimData, meetEvents)
          pendingRelay = null
        }
        break
      }

      default:
        // A1, B1, B2, C2, C3, Z0 and unknown records are intentionally ignored
        break
    }
  }

  // Commit any trailing relay that had no F3 record
  if (pendingRelay) {
    commitRelay(pendingRelay, swimData, meetEvents)
  }

  // Build the flat swimmer list (unique by club+name+gender+DOB)
  const swimmers = buildSwimmerList(swimData)
  const clubNames = clubs.map((c) => c.teamFullName)

  return { clubNames, swimmers, swimData, meetEvents }
}

// ---------------------------------------------------------------------------
// Internal helpers used after the parse loop
// ---------------------------------------------------------------------------

function commitRelay(
  pending: IPendingRelay,
  swimData: ISwimDatum,
  meetEvents: MeetEvent[],
): void {
  const clubEntry = swimData.clubs.find((c) => c.teamCode === pending.teamCode)
  if (!clubEntry) return

  clubEntry.relayEntries.push({
    relayOnlyRecord: "0",
    isRelay: "1",
    relayTeam: relayLetterToNum(pending.relayDesignation),
    eventGender: pending.eventGender,
    eventStroke: pending.eventStroke,
    eventDistance: pending.eventDistance,
    eventNumber: pending.eventNumber,
    eventAge: pending.eventAge,
    seedTime: pending.seedTime,
    seedTimeCourse: pending.seedTimeCourse,
    prelimTime: "",
    prelimTimeCourse: "",
    swimOffTime: "",
    swimOffTimeCourse: "",
    finalsTime: "",
    finalsTimeCourse: "",
    prelimHeat: "",
    prelimLane: "",
    prelimPlace: "",
    finalsHeat: "",
    finalsLane: "",
    finalsPlace: "",
    finalsPoints: "",
    isExhibition: pending.isExhibition,
    alternateDesignation: pending.alternateDesignation,
    round: null,
    relayLegs: pending.relayLegs,
  })

  if (pending.eventNumber > 0 && !meetEvents.some((e) => e.eventNumber === pending.eventNumber)) {
    meetEvents.push({
      distance: String(pending.eventDistance),
      gender: pending.eventGender === "F" ? "Girls" : pending.eventGender === "M" ? "Boys" : "Mixed",
      ageGroup: pending.eventAge,
      eventNumber: pending.eventNumber,
      stroke: pending.eventStroke,
      isRelay: 1,
      course: "SCY",
      eventName: `${pending.eventGender === "F" ? "Girls" : "Boys"} ${pending.eventAge} ${pending.eventDistance} ${pending.eventStroke}`,
    })
  }
}

function buildSwimmerList(swimData: ISwimDatum) {
  const seen = new Set<string>()
  const swimmers: {
    club: string
    swimmerFirstName: string
    swimmerLastName: string
    swimmerGender: string
    swimmerDOB: string
    swimmerUSAId: string
    swimmerId: number
  }[] = []

  for (const club of swimData.clubs) {
    const clubName = club.teamFullName

    for (const entry of club.individualEntries) {
      const key = `${clubName}|${entry.swimmerFirstName}|${entry.swimmerLastName}|${entry.swimmerGender}|${entry.swimmerDOB}`
      if (!seen.has(key)) {
        seen.add(key)
        swimmers.push({
          club: clubName,
          swimmerFirstName: entry.swimmerFirstName,
          swimmerLastName: entry.swimmerLastName,
          swimmerGender: entry.swimmerGender,
          swimmerDOB: entry.swimmerDOB,
          swimmerUSAId: entry.swimmerUSAId,
          swimmerId: -1,
        })
      }
    }

    for (const relay of club.relayEntries) {
      for (const leg of relay.relayLegs) {
        const key = `${clubName}|${leg.swimmerFirstName}|${leg.swimmerLastName}|${leg.swimmerGender}|${leg.swimmerDOB}`
        if (!seen.has(key)) {
          seen.add(key)
          swimmers.push({
            club: clubName,
            swimmerFirstName: leg.swimmerFirstName,
            swimmerLastName: leg.swimmerLastName,
            swimmerGender: leg.swimmerGender,
            swimmerDOB: leg.swimmerDOB,
            swimmerUSAId: leg.swimmerUSAId,
            swimmerId: -1,
          })
        }
      }
    }
  }

  return swimmers
}
